import { useMemo } from 'react';
import styled from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { createReadableClass, getColorWithAlpha } from '@elfsight/widget-utils';
import tinycolor from 'tinycolor2';
import { meta } from '../../../../constants';
import { VerifiedBadge } from '../../../lib/components';
import { useSocialFeedSettings } from '../../../utils';
import { useHeaderData, useHeaderDisplayLogic } from '../../helpers';
import { HeaderName } from './HeaderName';

interface HeaderNamesProps {
  isMobile?: boolean;
}

export function HeaderNames({ isMobile }: HeaderNamesProps) {
  const { alias } = meta;
  const {
    name = '',
    username = '',
    verified = false,
    url = ''
  } = useHeaderData();
  const isLinkedin = alias === 'linkedin-feed';
  const { headerVerifiedBadgeColor, headerTextColor = '' } =
    useSocialFeedSettings();

  const currentAlpha = tinycolor(headerTextColor).getAlpha();
  const finalAlpha = currentAlpha * 0.7;
  const colorWithAlpha = getColorWithAlpha(headerTextColor, finalAlpha);
  const badgeColor = isLinkedin
    ? {
        ...headerVerifiedBadgeColor,
        color: colorWithAlpha
      }
    : headerVerifiedBadgeColor;

  const {
    shouldShowAvatar,
    shouldShowFollowButton,
    shouldShowUsername,
    shouldShowName,
    shouldShowBadge
  } = useHeaderDisplayLogic();

  const namesDisplayLogic = useMemo(() => {
    const hasValidName = !!name && shouldShowName;
    const hasValidUsername = !!username && shouldShowUsername;

    let primaryElement: 'name' | 'username' | null = null;

    if (hasValidName) {
      primaryElement = 'name';
    } else if (hasValidUsername) {
      primaryElement = 'username';
    }

    const shouldShowSecondaryUsername =
      shouldShowUsername && shouldShowName && hasValidName && hasValidUsername;

    const shouldShowVerifiedBadge = shouldShowBadge && verified;

    return {
      primaryElement,
      shouldShowSecondaryUsername,
      shouldShowVerifiedBadge,
      isCentered: isMobile || (!shouldShowAvatar && !shouldShowFollowButton)
    };
  }, [
    name,
    username,
    shouldShowName,
    shouldShowUsername,
    shouldShowBadge,
    verified,
    isMobile,
    shouldShowAvatar,
    shouldShowFollowButton
  ]);

  const {
    primaryElement,
    shouldShowSecondaryUsername,
    shouldShowVerifiedBadge,
    isCentered
  } = namesDisplayLogic;

  if (!primaryElement) {
    return null;
  }

  return (
    <Container $isCentered={isCentered}>
      <NameAndBadgeContainer>
        <HeaderName variant={primaryElement} />

        <Show when={shouldShowVerifiedBadge}>
          <VerifiedBadge url={url} color={badgeColor} />
        </Show>
      </NameAndBadgeContainer>

      <Show when={shouldShowSecondaryUsername}>
        <HeaderName variant="username" />
      </Show>
    </Container>
  );
}

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'header-names',
    element: 'container'
  })
})<{
  $isCentered: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: ${({ $isCentered }) =>
    $isCentered ? 'center' : 'flex-start'};
  gap: 8px;
  width: 100%;
  overflow: hidden;
`;

const NameAndBadgeContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'header-names',
    element: 'name-and-badge-container'
  })
})`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  position: relative;
  overflow: hidden;
`;
